/**
 * HWPX 파서 공유 상수/타입/유틸 (parser.ts에서 분리).
 * ZIP 한도, 섹션 공유 상태(SectionShared), walk 컨텍스트(WalkCtx), XML 유틸.
 */

import { DOMParser } from "@xmldom/xmldom"
import { KordocError } from "../utils.js"
import type { CellContext, IRBlock, ParseWarning } from "../types.js"
// WalkCtx.styleMap 타입 참조 — 타입 전용이라 styles.ts와의 순환은 컴파일 시 소거됨
import type { HwpxStyleMap } from "./styles.js"

// 256MB — rhwp 1만 건 실문서 서베이에서 section1.xml 단독 75.2MB(압축비 35:1) 정상
// 문서가 확인됨 (rhwp #1917). 종전 100MB 총합 컷은 대형 실문서를 ZIP bomb 으로 오인 거부.
export const MAX_DECOMPRESS_SIZE = 256 * 1024 * 1024
/** 손상 ZIP 복구 시 최대 엔트리 수 */
export const MAX_ZIP_ENTRIES = 500

/** ZIP bomb 가드 전용 에러 — per-section catch가 XML fatalError(PARTIAL_PARSE로 강등)와
 *  구분해 이것만 재던진다. KordocError 서브클래스라 sanitizeError allowlist는 그대로 통과 */
export class ZipBombError extends KordocError {
  constructor(message: string) {
    super(message)
    this.name = "ZipBombError"
  }
}

/** colSpan/rowSpan을 안전한 범위로 클램핑 */
export function clampSpan(val: number, max: number): number {
  return Math.max(1, Math.min(val, max))
}

/** XML DOM 재귀 최대 깊이 — 악성 파일의 스택 오버플로 방지.
 *  좌표계가 다른 hwp5 MAX_NEST_DEPTH(8, 표 중첩 단계)·filler/소스맵 16
 *  (표 중첩 단계)과 달리 이건 "XML 요소" 깊이라 표 1단이 여러 depth를
 *  소모한다 — 상수 통일 금지 (의미가 다름) */
export const MAX_XML_DEPTH = 200

/** 셀 컨텍스트 확장 — 중첩표/이미지/다중문단 블록과 제목셀 여부를 IRCell로 전달 (v3.0) */
export interface CellCtxEx extends CellContext {
  blocks?: IRBlock[]
  /** 중첩표/이미지 등 구조 콘텐츠 존재 — true일 때만 IRCell.blocks로 attach */
  hasStructure?: boolean
  isHeader?: boolean
  /**
   * cell.text 평탄화 조립용 임시 상태 (#52 후속) — 인라인 흐름(글자취급 표·같은 문단
   * 텍스트)이 "열려" 있어 다음 인라인 항목을 `\n`이 아니라 공백으로 이어야 하는지.
   * 문단 경계·블록/float 표에서 닫힌다(false). 최종 IRCell로는 나가지 않는 워크 전용 필드.
   */
  lineOpen?: boolean
}

export interface TableState {
  rows: CellContext[][]
  currentRow: CellContext[]
  cell: CellCtxEx | null
  /** hp:caption 텍스트 — IRTable.caption으로 전달 (v3.0) */
  caption?: string
  /** hp:caption 내부 블록(중첩표 포함) — IRTable.captionBlocks로 전달 (#55) */
  captionBlocks?: IRBlock[]
  /**
   * 글자취급(treatAsChar="1") 표 여부 — 부모 셀 텍스트 평탄화 시 앞뒤 텍스트와 같은 줄로
   * 공백 연결할지 판단 (#52 후속). 블록/float 표(기본 undefined)는 종전대로 `\n`.
   */
  inline?: boolean
}

/** 섹션 간 공유 상태 — 자동번호 카운터, 머리말/꼬리말, 변경추적 */
export interface SectionShared {
  /** numbering id → 레벨별(1..10) 카운터. -1 = 미사용(start값으로 초기화 — 0은 start="0"의 유효값) */
  numState: Map<string, number[]>
  pageText: { headers: string[]; footers: string[] }
  track: { deleteDepth: number; warned: boolean }
  /** content.hpf kordoc-layout 메타 ("default"|"gongmun") — 자사 생성 파일 왕복 채널
   *  게이트. null/미설정 = 외래 파일 (id 기반 인라인 강조·인용 복원 꺼짐) */
  kordocLayout?: string | null
  /** 표 후행 빈 열(앵커 있는 입력란) 보존 — ParseOptions.keepTrailingEmptyCols (#47) */
  keepTrailingEmptyCols?: boolean
}

export function createSectionShared(): SectionShared {
  return { numState: new Map(), pageText: { headers: [], footers: [] }, track: { deleteDepth: 0, warned: false } }
}

/** walk 함수들이 공유하는 파싱 컨텍스트 — 개별 optional 파라미터를 하나로 묶어 시그니처 안정화 */
export interface WalkCtx {
  styleMap?: HwpxStyleMap
  warnings?: ParseWarning[]
  sectionNum?: number
  shared: SectionShared
  /** secPr outlineShapeIDRef — 개요(OUTLINE) 문단이 사용하는 numbering id */
  outlineNumId?: string
}

/** xmldom DOMParser 생성 — onError 콜백으로 malformed XML 경고 수집 */
export function createXmlParser(warnings?: ParseWarning[]): DOMParser {
  return new DOMParser({
    onError(level: "warning" | "error" | "fatalError", msg: string) {
      if (level === "fatalError") throw new KordocError(`XML 파싱 실패: ${msg}`)
      warnings?.push({ code: "MALFORMED_XML", message: `XML ${level === "warning" ? "경고" : "오류"}: ${msg}` })
    },
  })
}

/** 수집된 머리말/꼬리말을 본문 앞/뒤 문단으로 배치 */
export function applyPageText(blocks: IRBlock[], shared: SectionShared): void {
  const { headers, footers } = shared.pageText
  if (headers.length > 0) {
    blocks.unshift(...headers.map(t => ({ type: "paragraph" as const, text: t, pageNumber: 1 })))
  }
  if (footers.length > 0) {
    blocks.push(...footers.map(t => ({ type: "paragraph" as const, text: t })))
  }
}

/** 자식 중 지정된 localName(접두사 제거)을 가진 첫 번째 Element 반환 */
export function findChildByLocalName(parent: Element, name: string): Element | null {
  const children = parent.childNodes
  if (!children) return null
  for (let i = 0; i < children.length; i++) {
    const ch = children[i] as Element
    if (ch.nodeType !== 1) continue
    const tag = (ch.tagName || ch.localName || "").replace(/^[^:]+:/, "")
    if (tag === name) return ch
  }
  return null
}

/** 노드 내 모든 텍스트를 재귀적으로 추출 (MAX_XML_DEPTH 가드 — 악성 심층 XML 스택 오버플로 방지) */
export function extractTextFromNode(node: Node, depth: number = 0): string {
  let result = ""
  if (depth > MAX_XML_DEPTH) return result
  const children = node.childNodes
  if (!children) return result
  for (let i = 0; i < children.length; i++) {
    const child = children[i]
    if (child.nodeType === 3) result += child.textContent || ""
    else if (child.nodeType === 1) result += extractTextFromNode(child, depth + 1)
  }
  return result.trim()
}
